/**
 * The languages this platform will write outreach in, and how likely a business
 * contact in a given country is to prefer English.
 *
 * WHY A PREVALENCE NUMBER RATHER THAN A LIST OF "FOREIGN" COUNTRIES
 *
 * The ask was "outreach in the person's language, but only where it is needed".
 * Both halves matter. Writing to a Dutch operations director in Dutch is not
 * thoughtful, it is slightly odd: their working language is English and the
 * message now reads as a translation. Writing to an Indonesian founder in
 * English costs the reply outright. The line between those two is not a
 * continent, it is how much business in that market actually happens in
 * English, so that is the number the decision is made on.
 *
 * The Philippines is the case that proves it. It is next door to Indonesia,
 * it is one of the two markets this platform sells into, and it sits at the top
 * of this table rather than the bottom: English is an official language there
 * and the business default. A rule built on "Southeast Asia" would get one of
 * our two markets exactly wrong.
 *
 * WHERE THE NUMBERS COME FROM
 *
 * They are a judgement, not a measurement, and they are written down so they can
 * be argued with. Each is a rough answer to one question: out of a hundred
 * white-collar people in this country who might receive a cold message, how many
 * would rather read it in English than in the local language. They are tuned for
 * the decision they feed, which is a single threshold, so the difference between
 * 20 and 25 never matters and the difference between 40 and 60 always does.
 *
 * Anything not in the table is treated as English-comfortable, because a country
 * nobody has thought about is a country nobody has evidence about, and guessing
 * a language wrong is a worse failure than defaulting to English.
 */

export interface LanguageDef {
  /** BCP 47 primary subtag. */
  code: string
  /** What we call it in the console, in English. */
  name: string
  /** What speakers call it. Shown beside the English name so a picker is not patronising. */
  endonym: string
  /** Right-to-left scripts need the draft editor to say so. */
  rtl?: boolean
  /**
   * Can somebody on this team read a draft in it well enough to catch a bad one?
   *
   * This is the gate on *inferring* a language, not on offering it. A message
   * generated in a language nobody here reads is not personalisation, it is an
   * unreviewable paragraph going out under somebody's name, and the first time
   * anyone finds out it was wrong is a reply nobody can read either.
   *
   * So a market whose language nobody covers falls back to English, which is
   * always at least legible to both sides. Picking one by hand still works: an
   * operator who has a colleague or a translator has information this table
   * does not.
   */
  reviewable: boolean
  /**
   * Can this language be written properly in plain ASCII, with no accented
   * letter and no other script?
   *
   * This is what a phone can type today. The device engine puts each character
   * into the app with `adb shell input text`, which cannot type anything above
   * code point 127, and the copy validator refuses such a draft for the same
   * reason. So a draft in a language that needs those letters, on a channel a
   * phone types, is refused at generation, refused again on the rewrite, and
   * parked for a person who cannot fix it either, because the fault is the
   * alphabet and not the words.
   *
   * Set only where it is plainly true. Indonesian, Malay and Filipino are written
   * in unaccented Latin letters in ordinary business use, and so is English.
   * French, Spanish, German and the rest need accents or other letters in
   * ordinary sentences, and Arabic, Urdu and Hindi are other scripts entirely.
   * Stripping the accents off French is not writing French, so it is not on
   * this list. Absent reads as no.
   */
  plainAscii?: boolean
}

/**
 * Every language the platform will generate in.
 *
 * Every one of these can be *chosen*. Only the ones marked `reviewable` are
 * ever *inferred*, and that flag is the real gate: generating Japanese nobody
 * on this team can check is not a feature, it is an unreviewable message going
 * out under somebody's name.
 *
 * The reviewable set is a fact about the people here today, not about the
 * languages. It is English, Indonesian, Malay and Filipino (Agnes), French and
 * Arabic (Basel), Urdu and Hindi (Fahad). Somebody joining changes this list.
 */
export const OUTREACH_LANGUAGES: readonly LanguageDef[] = [
  { code: 'en', name: 'English', endonym: 'English', reviewable: true, plainAscii: true },
  { code: 'id', name: 'Indonesian', endonym: 'Bahasa Indonesia', reviewable: true, plainAscii: true },
  { code: 'fr', name: 'French', endonym: 'Français', reviewable: true },
  { code: 'ar', name: 'Arabic', endonym: 'العربية', rtl: true, reviewable: true },
  { code: 'ur', name: 'Urdu', endonym: 'اردو', rtl: true, reviewable: true },
  { code: 'hi', name: 'Hindi', endonym: 'हिन्दी', reviewable: true },
  { code: 'es', name: 'Spanish', endonym: 'Español', reviewable: false },
  { code: 'pt', name: 'Portuguese', endonym: 'Português', reviewable: false },
  { code: 'de', name: 'German', endonym: 'Deutsch', reviewable: false },
  { code: 'it', name: 'Italian', endonym: 'Italiano', reviewable: false },
  { code: 'nl', name: 'Dutch', endonym: 'Nederlands', reviewable: false },
  { code: 'tr', name: 'Turkish', endonym: 'Türkçe', reviewable: false },
  { code: 'ms', name: 'Malay', endonym: 'Bahasa Melayu', reviewable: true, plainAscii: true },
  { code: 'tl', name: 'Filipino', endonym: 'Filipino', reviewable: true, plainAscii: true },
  { code: 'vi', name: 'Vietnamese', endonym: 'Tiếng Việt', reviewable: false },
  { code: 'th', name: 'Thai', endonym: 'ไทย', reviewable: false },
  { code: 'ja', name: 'Japanese', endonym: '日本語', reviewable: false },
  { code: 'ko', name: 'Korean', endonym: '한국어', reviewable: false },
  { code: 'zh', name: 'Chinese', endonym: '中文', reviewable: false },
  { code: 'ru', name: 'Russian', endonym: 'Русский', reviewable: false },
  { code: 'pl', name: 'Polish', endonym: 'Polski', reviewable: false },
]

export const LANGUAGE_CODES: readonly string[] = OUTREACH_LANGUAGES.map((language) => language.code)

const BY_CODE = new Map(OUTREACH_LANGUAGES.map((language) => [language.code, language]))

export function languageByCode(code: string | null | undefined): LanguageDef | null {
  if (code === null || code === undefined) return null
  return BY_CODE.get(code.trim().toLowerCase().split('-')[0] ?? '') ?? null
}

/** Can a draft in this language be checked by somebody here before it goes? */
export function isReviewable(code: string | null | undefined): boolean {
  return languageByCode(code)?.reviewable === true
}

/**
 * Can a phone type a draft in this language? See `plainAscii` above.
 *
 * An unknown code answers no. A language nobody has described is one nobody has
 * checked the alphabet of, and the cost of guessing yes is a draft that parks.
 */
export function writtenInPlainAscii(code: string | null | undefined): boolean {
  return languageByCode(code)?.plainAscii === true
}

/** English name, for a sentence. Falls back to the code so a stray value is still legible. */
export function languageName(code: string | null | undefined): string {
  return languageByCode(code)?.name ?? (code ?? 'English')
}

export interface MarketDef {
  /** ISO 3166-1 alpha-2. */
  country: string
  /** How we name it in a sentence. */
  name: string
  /** The language to write in when English is not the right choice here. */
  language: string
  /**
   * Roughly how many business contacts out of a hundred would rather read a cold
   * message in English. See the file header on where this comes from.
   */
  englishBusinessUse: number
  /**
   * Whether "Assalamu alaikum" is an unremarkable everyday greeting between
   * strangers here, rather than a statement about either party. This is a fact
   * about the market's conversational norm, not about any individual, and it is
   * only ever one half of the greeting decision.
   */
  salaamIsOrdinary?: boolean
}

/**
 * The markets, with the two facts the drafting decision needs.
 *
 * Ordered by region rather than alphabetically, because the arguments about
 * these numbers happen region by region.
 */
export const MARKETS: readonly MarketDef[] = [
  // Southeast Asia. The two that matter most to this business sit at opposite
  // ends of the table, which is the whole reason the rule is a number.
  { country: 'ID', name: 'Indonesia', language: 'id', englishBusinessUse: 25, salaamIsOrdinary: true },
  { country: 'PH', name: 'the Philippines', language: 'tl', englishBusinessUse: 90 },
  { country: 'SG', name: 'Singapore', language: 'en', englishBusinessUse: 95 },
  { country: 'MY', name: 'Malaysia', language: 'ms', englishBusinessUse: 65, salaamIsOrdinary: true },
  { country: 'BN', name: 'Brunei', language: 'ms', englishBusinessUse: 70, salaamIsOrdinary: true },
  { country: 'TH', name: 'Thailand', language: 'th', englishBusinessUse: 20 },
  { country: 'VN', name: 'Vietnam', language: 'vi', englishBusinessUse: 25 },
  { country: 'KH', name: 'Cambodia', language: 'en', englishBusinessUse: 45 },

  // East Asia.
  { country: 'JP', name: 'Japan', language: 'ja', englishBusinessUse: 20 },
  { country: 'KR', name: 'South Korea', language: 'ko', englishBusinessUse: 30 },
  { country: 'CN', name: 'China', language: 'zh', englishBusinessUse: 20 },
  { country: 'TW', name: 'Taiwan', language: 'zh', englishBusinessUse: 30 },
  { country: 'HK', name: 'Hong Kong', language: 'zh', englishBusinessUse: 75 },

  // South Asia. English is the working language of business across this region
  // at the level this platform writes to, which is why Urdu and Hindi are
  // offered as a deliberate choice and never inferred.
  { country: 'IN', name: 'India', language: 'hi', englishBusinessUse: 80 },
  { country: 'PK', name: 'Pakistan', language: 'ur', englishBusinessUse: 75, salaamIsOrdinary: true },
  { country: 'BD', name: 'Bangladesh', language: 'en', englishBusinessUse: 60, salaamIsOrdinary: true },
  { country: 'LK', name: 'Sri Lanka', language: 'en', englishBusinessUse: 70 },

  // Middle East and North Africa.
  { country: 'AE', name: 'the United Arab Emirates', language: 'ar', englishBusinessUse: 75, salaamIsOrdinary: true },
  { country: 'SA', name: 'Saudi Arabia', language: 'ar', englishBusinessUse: 45, salaamIsOrdinary: true },
  { country: 'QA', name: 'Qatar', language: 'ar', englishBusinessUse: 70, salaamIsOrdinary: true },
  { country: 'KW', name: 'Kuwait', language: 'ar', englishBusinessUse: 60, salaamIsOrdinary: true },
  { country: 'BH', name: 'Bahrain', language: 'ar', englishBusinessUse: 70, salaamIsOrdinary: true },
  { country: 'OM', name: 'Oman', language: 'ar', englishBusinessUse: 55, salaamIsOrdinary: true },
  { country: 'EG', name: 'Egypt', language: 'ar', englishBusinessUse: 35, salaamIsOrdinary: true },
  { country: 'JO', name: 'Jordan', language: 'ar', englishBusinessUse: 55, salaamIsOrdinary: true },
  { country: 'LB', name: 'Lebanon', language: 'ar', englishBusinessUse: 60 },
  { country: 'MA', name: 'Morocco', language: 'fr', englishBusinessUse: 25, salaamIsOrdinary: true },
  { country: 'TN', name: 'Tunisia', language: 'fr', englishBusinessUse: 25, salaamIsOrdinary: true },
  { country: 'DZ', name: 'Algeria', language: 'fr', englishBusinessUse: 20, salaamIsOrdinary: true },
  { country: 'TR', name: 'Turkey', language: 'tr', englishBusinessUse: 25 },

  // Europe. Northern Europe is the reason this is a number and not a border:
  // writing Dutch to a Dutch director reads as a translation, not a courtesy.
  { country: 'NL', name: 'the Netherlands', language: 'nl', englishBusinessUse: 90 },
  { country: 'SE', name: 'Sweden', language: 'en', englishBusinessUse: 90 },
  { country: 'NO', name: 'Norway', language: 'en', englishBusinessUse: 90 },
  { country: 'DK', name: 'Denmark', language: 'en', englishBusinessUse: 90 },
  { country: 'FI', name: 'Finland', language: 'en', englishBusinessUse: 85 },
  { country: 'DE', name: 'Germany', language: 'de', englishBusinessUse: 65 },
  { country: 'AT', name: 'Austria', language: 'de', englishBusinessUse: 65 },
  { country: 'CH', name: 'Switzerland', language: 'de', englishBusinessUse: 70 },
  { country: 'GB', name: 'the United Kingdom', language: 'en', englishBusinessUse: 100 },
  { country: 'IE', name: 'Ireland', language: 'en', englishBusinessUse: 100 },
  { country: 'FR', name: 'France', language: 'fr', englishBusinessUse: 40 },
  { country: 'BE', name: 'Belgium', language: 'nl', englishBusinessUse: 75 },
  { country: 'ES', name: 'Spain', language: 'es', englishBusinessUse: 35 },
  { country: 'PT', name: 'Portugal', language: 'pt', englishBusinessUse: 60 },
  { country: 'IT', name: 'Italy', language: 'it', englishBusinessUse: 35 },
  { country: 'PL', name: 'Poland', language: 'pl', englishBusinessUse: 55 },
  // Central Europe, added when a real list turned up a prospect in Brno and the
  // table had nothing to say about them. English business use is high across
  // this group at the level this platform writes to, so they resolve to English
  // and are here for the country to be *known* rather than to change a language.
  { country: 'CZ', name: 'Czechia', language: 'en', englishBusinessUse: 60 },
  { country: 'SK', name: 'Slovakia', language: 'en', englishBusinessUse: 55 },
  { country: 'HU', name: 'Hungary', language: 'en', englishBusinessUse: 55 },
  { country: 'RO', name: 'Romania', language: 'en', englishBusinessUse: 65 },
  { country: 'BG', name: 'Bulgaria', language: 'en', englishBusinessUse: 55 },
  { country: 'GR', name: 'Greece', language: 'en', englishBusinessUse: 60 },
  { country: 'HR', name: 'Croatia', language: 'en', englishBusinessUse: 60 },
  { country: 'RS', name: 'Serbia', language: 'en', englishBusinessUse: 55 },
  { country: 'EE', name: 'Estonia', language: 'en', englishBusinessUse: 80 },
  { country: 'LV', name: 'Latvia', language: 'en', englishBusinessUse: 70 },
  { country: 'LT', name: 'Lithuania', language: 'en', englishBusinessUse: 70 },
  { country: 'SI', name: 'Slovenia', language: 'en', englishBusinessUse: 70 },
  { country: 'LU', name: 'Luxembourg', language: 'fr', englishBusinessUse: 80 },
  { country: 'IS', name: 'Iceland', language: 'en', englishBusinessUse: 90 },
  { country: 'MT', name: 'Malta', language: 'en', englishBusinessUse: 90 },
  { country: 'CY', name: 'Cyprus', language: 'en', englishBusinessUse: 80 },
  { country: 'RU', name: 'Russia', language: 'ru', englishBusinessUse: 25 },
  { country: 'UA', name: 'Ukraine', language: 'ru', englishBusinessUse: 35 },

  // The Americas.
  { country: 'US', name: 'the United States', language: 'en', englishBusinessUse: 100 },
  { country: 'CA', name: 'Canada', language: 'en', englishBusinessUse: 95 },
  { country: 'MX', name: 'Mexico', language: 'es', englishBusinessUse: 30 },
  { country: 'BR', name: 'Brazil', language: 'pt', englishBusinessUse: 25 },
  { country: 'AR', name: 'Argentina', language: 'es', englishBusinessUse: 35 },
  { country: 'CO', name: 'Colombia', language: 'es', englishBusinessUse: 25 },
  { country: 'CL', name: 'Chile', language: 'es', englishBusinessUse: 35 },
  { country: 'PE', name: 'Peru', language: 'es', englishBusinessUse: 25 },

  // Africa and Oceania.
  { country: 'NG', name: 'Nigeria', language: 'en', englishBusinessUse: 90 },
  { country: 'KE', name: 'Kenya', language: 'en', englishBusinessUse: 90 },
  { country: 'GH', name: 'Ghana', language: 'en', englishBusinessUse: 90 },
  { country: 'ZA', name: 'South Africa', language: 'en', englishBusinessUse: 95 },
  { country: 'AU', name: 'Australia', language: 'en', englishBusinessUse: 100 },
  { country: 'NZ', name: 'New Zealand', language: 'en', englishBusinessUse: 100 },
]

const BY_COUNTRY = new Map(MARKETS.map((market) => [market.country, market]))

export function marketByCountry(code: string | null | undefined): MarketDef | null {
  if (code === null || code === undefined) return null
  return BY_COUNTRY.get(code.trim().toUpperCase()) ?? null
}

/**
 * Below this, write in the local language. At or above it, write in English.
 *
 * 50 is the point where the choice stops being obvious in either direction, and
 * a threshold placed there means every judgement in the table that is merely
 * imprecise still produces the right answer. It is exported so it appears in the
 * console's own explanation of what it is doing.
 */
export const ENGLISH_COMFORT_THRESHOLD = 50
